'use client'

import { useEffect, useState, type ReactNode } from 'react'
import type { YMarketOrder } from '@/lib/types'
import type { CdekTrackingData } from '@/lib/api/schemas/cdek.schema'
import type { MsgType } from '@/lib/order-storage'
import { FieldRow, FieldEditable } from '@/components/ui/FieldRow'
import { MessageHeader, MessageBlock, SentTextNote } from '@/components/orders/MessageBlock'
import type { MessageDispatch } from '@/lib/types'
import { messageStateFor } from '@/lib/message-state'
import {
  EM_DASH, CDEK_STATUS_TONES,
  formatAddress, getRegionName, paymentTotal, capitalize, orderShipment,
} from '@/lib/order-helpers'
import { guessGreetingIndex } from '@/lib/name-heuristics'
import { getCdekTransitPlan } from '@/lib/cdek-transit'
import { latestCdekStatus } from '@/lib/cdek-status-history'
import { assignedCourier, courierMessageEmptyText } from '@/lib/courier-assignment'
import { CustomerBadge } from '@/components/customers/CustomerBadge'
import { YMarketChatPanel, type PendingChatDelivery } from '@/components/orders/YMarketChatPanel'
import type { OrderChatHistory } from '@/lib/ymarket-chat'
import type { ChatSendFlight, ChatSendOrigin } from '@/lib/chat-motion'
import { LocalDateTime } from '@/components/ui/LocalDateTime'
import { ProductThumbnail } from '@/components/orders/ProductThumbnail'
import { orderLinePiece, PieceReference } from '@/components/piece/PieceReference'
import { CdekReturnCard } from '@/components/orders/CdekReturnCard'
import { CdekShipmentDirection } from '@/lib/cdek-domain'
import { OrderItemFinancials } from '@/components/orders/OrderItemFinancials'

const CDEK_DELIVERY_MODES: Record<string, string> = {
  '1': 'Дверь → дверь',
  '2': 'Дверь → склад',
  '3': 'Склад → дверь',
  '4': 'Склад → склад',
  '6': 'Дверь → постамат',
  '7': 'Склад → постамат',
  '8': 'Постамат → дверь',
  '9': 'Постамат → склад',
  '10': 'Постамат → постамат',
}

function OutboundCdekSection({
  collapsible,
  open,
  onToggle,
  children,
}: {
  collapsible: boolean
  open: boolean
  onToggle: (open: boolean) => void
  children: ReactNode
}) {
  if (!collapsible) return <>{children}</>

  return (
    <details
      open={open}
      onToggle={(event) => onToggle(event.currentTarget.open)}
      className="mb-8"
    >
      {children}
    </details>
  )
}

function cdekDate(value?: string, withTime = false): string {
  if (!value) return EM_DASH
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString('ru-RU', withTime
    ? { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }
    : { day: 'numeric', month: 'long', year: 'numeric' })
}

function cdekLocation(location?: CdekTrackingData['to_location']): string {
  if (!location) return EM_DASH
  return [location.city, location.region, location.address].filter(Boolean).join(', ') || EM_DASH
}

function cdekContact(contact?: CdekTrackingData['recipient']): string {
  if (!contact) return EM_DASH
  return [contact.name || contact.company, contact.phones?.map((phone) => phone.number).join(', '), contact.email]
    .filter(Boolean)
    .join(' · ') || EM_DASH
}

export interface DbsDetailProps {
  order: YMarketOrder
  buyerName: string
  nameParts: string[]
  greetingNameIdx: number | null
  manualShipDate: string
  manualShipTime: string
  manualTrack: string
  cdekEstimate: string | null
  cdekTracking: CdekTrackingData | null
  returnCdekTracking: CdekTrackingData | null
  returnCdekTrackingLoading: boolean
  messageStates: MessageDispatch[]
  copied: boolean
  copiedFirst: boolean
  copiedCourier: boolean
  sendingType: MsgType | null
  sendError: string
  isReady: boolean
  handoffAccepted: boolean
  missingFields: string[]
  templateText: string | null
  firstMessageText: string | null
  firstMsgMissing: string[]
  courierMessageText: string | null
  chatHistory: OrderChatHistory | null
  chatLoading: boolean
  chatError: string
  chatOpen: boolean
  pendingChatDelivery: PendingChatDelivery | null
  chatSendFlight: ChatSendFlight | null
  onChatOpenChange: (open: boolean) => void
  onChatSendFlightComplete: () => void
  onRefreshChat: () => void
  onShipDateChange: (v: string) => void
  onShipTimeChange: (v: string) => void
  onTrackChange: (v: string) => void
  onGreetingPartChange: (idx: number) => void
  onCopy: () => void
  onCopyFirst: () => void
  onCopyCourier: () => void
  onSendFirst: (origin: ChatSendOrigin) => void
  onSendConfirm: (origin: ChatSendOrigin) => void
  onSendCourier: (origin: ChatSendOrigin) => void
  onToggleManualMark: (kind: MsgType) => void
  markingManual: MsgType | null
}

export function DbsDetail({
  order, buyerName, nameParts, greetingNameIdx,
  manualShipDate, manualShipTime, manualTrack,
  cdekEstimate, cdekTracking, returnCdekTracking, returnCdekTrackingLoading,
  messageStates, copied, copiedFirst, copiedCourier, sendingType, markingManual, sendError,
  isReady, handoffAccepted, missingFields, templateText, firstMessageText, firstMsgMissing, courierMessageText,
  chatHistory, chatLoading, chatError, chatOpen, pendingChatDelivery, chatSendFlight, onChatOpenChange, onChatSendFlightComplete, onRefreshChat,
  onShipDateChange, onShipTimeChange, onTrackChange,
  onGreetingPartChange,
  onCopy, onCopyFirst, onCopyCourier, onSendFirst, onSendConfirm, onSendCourier, onToggleManualMark,
}: DbsDetailProps) {
  const hasApiTrack = !!order.delivery?.tracks?.[0]?.trackCode
  const total = paymentTotal(order)
  const singleItemPrices = order.items?.length === 1 ? order.prices : undefined
  const cdekCurrent = latestCdekStatus(cdekTracking?.statuses)
  const courier = assignedCourier(cdekTracking)
  const cdekTransitPlan = getCdekTransitPlan(cdekTracking)
  const cdekDetail = cdekTracking?.delivery_detail
  const cdekProblems = Array.isArray(cdekTracking?.delivery_problem)
    ? cdekTracking.delivery_problem as Array<{ code?: string; create_date?: string }>
    : []
  const cdekCalls = cdekTracking?.calls && typeof cdekTracking.calls === 'object'
    ? cdekTracking.calls as {
        failed_calls?: Array<{ date_time?: string; reason_code?: number }>
        rescheduled_calls?: Array<{ date_time?: string; date_next?: string; time_next?: string; comment?: string }>
      }
    : {}
  const returnShipment = orderShipment(order)
  const [outboundOpen, setOutboundOpen] = useState(
    returnShipment?.direction !== CdekShipmentDirection.Return,
  )

  useEffect(() => {
    setOutboundOpen(returnShipment?.direction !== CdekShipmentDirection.Return)
  }, [order.orderId, returnShipment?.direction])

  return (
    <div className="grid grid-cols-1 2xl:grid-cols-2 gap-x-8 gap-y-0 items-start">
      {/* Left column — order fields */}
      <div>
      <div className="surface-card rounded-md mb-6 md:mb-8 px-4 sm:px-6 py-1">
        {order.customerStats ? (
          <FieldEditable label="Покупатель">
            <CustomerBadge
              customerId={order.customerStats.customerId}
              name={order.customerStats.name || buyerName}
              city={order.customerStats.city}
              phone={order.customerStats.phone}
            />
          </FieldEditable>
        ) : <FieldRow label="Покупатель" value={buyerName} />}

        {order.items && order.items.length > 0 && (
          <div className="py-3.5 border-b border-rule last:border-0">
            <div className="text-[11px] tracking-[0.08em] uppercase text-ink-muted font-semibold mb-2.5">
              Товары
            </div>
            <div className="space-y-1.5">
              {order.items.map((item, i) => {
                const product = item.offerName || item.offerId || 'Товар'
                return (
                <div key={i}>
                  <div className="flex items-center gap-3 text-[14px]">
                    <ProductThumbnail item={item} label={`Фото: ${product}`} className="size-9" />
                    <span className="flex min-w-0 flex-1 truncate text-ink">
                      <PieceReference piece={orderLinePiece(item, product)} originLabel={`заказу №${order.orderId}`} />
                    </span>
                    <span className="text-ink-faint text-[12px] tab-nums">×{item.count || 1}</span>
                  </div>
                  <OrderItemFinancials item={item} fallbackPrices={singleItemPrices} className="ml-12" />
                </div>
              )})}
            </div>
          </div>
        )}

        <FieldRow label="Адрес" value={formatAddress(order)} />
        <FieldRow label="Город" value={order.delivery?.courier?.address?.city || EM_DASH} />
        <FieldRow label="Регион" value={getRegionName(order)} />
        <FieldRow label="Заказ оформлен" value={<LocalDateTime value={order.creationDate} />} />

        <FieldEditable label="Отправка">
          <input
            type="date"
            value={manualShipDate}
            onChange={(e) => onShipDateChange(e.target.value)}
            className="input"
          />
        </FieldEditable>

        <FieldEditable label="Время передачи">
          <input
            type="time"
            value={manualShipTime}
            onChange={(e) => onShipTimeChange(e.target.value)}
            className="input"
          />
        </FieldEditable>

        <FieldRow label="Дни в пути" value={
          <div className="flex items-center gap-3">
            <span className="text-ink-2 font-medium tab-nums">
              {cdekTransitPlan ? `${cdekTransitPlan.days} дн.` : 'СДЭК не указал срок'}
            </span>
            {cdekTransitPlan ? (
              <span className="text-[12px] text-gold-deep font-medium">СДЭК · план</span>
            ) : cdekEstimate && (
              <span className="text-[12px] text-gold-deep font-medium">
                СДЭК · {cdekEstimate}
              </span>
            )}
          </div>
        } />

        <FieldRow
          label="Доставка"
          value={cdekTransitPlan ? cdekDate(cdekTransitPlan.plannedDeliveryDate) : 'СДЭК не указал план'}
          accent
        />

        {order.delivery?.serviceName && (
          <FieldRow label="ТК" value={order.delivery.serviceName} />
        )}

        <FieldEditable label="Трек-номер">
          {hasApiTrack ? (
            <span className="text-[14px] tab-nums text-ink-2">
              {order.delivery!.tracks!.map((t) => t.trackCode).join(', ')}
            </span>
          ) : (
            <input
              type="text"
              placeholder="Введите трек-номер"
              value={manualTrack}
              onChange={(e) => onTrackChange(e.target.value)}
              className="input w-full sm:w-64 tab-nums"
            />
          )}
        </FieldEditable>

        {cdekCurrent && (() => {
          const tone = CDEK_STATUS_TONES[cdekCurrent.code] || 'bg-bone text-ink-muted border-rule-strong'
          return (
            <FieldEditable label="СДЭК статус">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`px-2.5 py-1 rounded-md text-[11px] tracking-[0.06em] uppercase font-medium border ${tone}`}>
                  {cdekCurrent.name}
                </span>
                {cdekCurrent.city && <span className="text-[12px] text-ink-muted">{cdekCurrent.city}</span>}
                {cdekCurrent.date_time && <span className="text-[12px] text-ink-faint">{cdekDate(cdekCurrent.date_time, true)}</span>}
              </div>
            </FieldEditable>
          )
        })()}

        {total != null && (
          <div className="flex items-baseline justify-between -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 mt-1 border-t border-rule bg-bone/40 rounded-b-md gap-3">
            <span className="text-[11px] tracking-[0.08em] uppercase text-ink-muted font-semibold">
              {order.paymentType === 'PREPAID' ? 'Предоплата'
                : order.paymentType === 'POSTPAID' ? 'При получении'
                : 'Итого'}
            </span>
            <span className="text-[22px] font-semibold text-ink tab-nums leading-none">
              {total.toLocaleString('ru-RU')}&thinsp;₽
            </span>
          </div>
        )}
      </div>

      {returnShipment?.direction === CdekShipmentDirection.Return && (
        <CdekReturnCard
          shipment={returnShipment}
          tracking={returnCdekTracking}
          loading={returnCdekTrackingLoading}
        />
      )}

      {cdekTracking && (
        <OutboundCdekSection
          collapsible={returnShipment?.direction === CdekShipmentDirection.Return}
          open={outboundOpen}
          onToggle={setOutboundOpen}
        >
          {returnShipment?.direction === CdekShipmentDirection.Return && (
            <summary className="surface-card rounded-md px-4 sm:px-6 py-3 cursor-pointer text-[11px] font-semibold text-ink-muted hover:text-ink flex flex-wrap items-center justify-between gap-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold">
              <span className="flex items-center gap-2">
                <span aria-hidden="true">{outboundOpen ? '▾' : '›'}</span>
                Исходная доставка к покупателю
              </span>
              <span className="tab-nums">
                {cdekTracking.cdek_number || manualTrack}
                {cdekCurrent?.name ? ` · ${cdekCurrent.name}` : ''}
              </span>
            </summary>
          )}
          <div className={`surface-card rounded-md overflow-hidden ${returnShipment?.direction === CdekShipmentDirection.Return ? 'mt-2' : 'mb-8'}`}>
          <div className="px-4 sm:px-6 py-4 border-b border-rule bg-bone/35 flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
            <div>
              <div className="eyebrow">СДЭК · данные накладной</div>
              <div className="mt-1 text-[17px] font-semibold tab-nums">{cdekTracking.cdek_number || manualTrack}</div>
              {cdekTracking.number && <div className="mt-1 text-[10px] text-ink-faint tab-nums">Внутренний № {cdekTracking.number}</div>}
            </div>
            {cdekCurrent && (
              <span className={`px-2.5 py-1 text-[10px] font-semibold border ${CDEK_STATUS_TONES[cdekCurrent.code] || 'bg-bone text-ink-muted border-rule-strong'}`}>
                {cdekCurrent.name}
              </span>
            )}
          </div>

          {courier && (
            <div className="mx-5 mt-5 p-4 bg-gold/10 border border-gold/30">
              <div className="eyebrow text-gold-deep">Назначенный курьер</div>
              <div className="mt-2 flex items-baseline gap-x-4 gap-y-1 flex-wrap">
                <span className="text-[15px] font-semibold">{courier.name || 'Имя не передано'}</span>
                {courier.phone && (
                  <a href={`tel:${courier.phone}`} className="text-[14px] font-semibold text-gold-deep hover:underline tab-nums">
                    {courier.phone}
                  </a>
                )}
              </div>
              {(courier.car_model || courier.registration_mark) && (
                <div className="mt-1 text-[11px] text-ink-muted">
                  {[courier.car_model, courier.registration_mark].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          )}

          <div className="px-4 sm:px-6 py-1">
            <FieldRow label="Маршрут" value={`${cdekLocation(cdekTracking.from_location)} → ${cdekLocation(cdekTracking.to_location)}`} />
            <FieldRow label="Режим" value={CDEK_DELIVERY_MODES[String(cdekTracking.delivery_mode)] || String(cdekTracking.delivery_mode || EM_DASH)} />
            <FieldRow label="План доставки" value={cdekDate(cdekTracking.planned_delivery_date)} accent />
            {cdekTracking.delivery_date && <FieldRow label="Факт доставки" value={cdekDate(cdekTracking.delivery_date)} />}
            <FieldRow label="Получатель" value={cdekContact(cdekTracking.recipient)} />
            <FieldRow label="Отправитель" value={cdekContact(cdekTracking.sender)} />
            {(cdekTracking.shipment_point || cdekTracking.delivery_point) && (
              <FieldRow label="Пункты" value={[cdekTracking.shipment_point && `отправка ${cdekTracking.shipment_point}`, cdekTracking.delivery_point && `выдача ${cdekTracking.delivery_point}`].filter(Boolean).join(' · ')} />
            )}
            {cdekTracking.packages?.length ? (
              <FieldRow
                label="Места"
                value={cdekTracking.packages.map((pkg, index) => [
                  `${index + 1} место`,
                  pkg.weight != null ? `${pkg.weight} г` : '',
                  pkg.length && pkg.width && pkg.height ? `${pkg.length}×${pkg.width}×${pkg.height} см` : '',
                ].filter(Boolean).join(' · ')).join('; ')}
              />
            ) : null}
            {cdekDetail?.delivery_sum != null && (
              <FieldRow label="Стоимость СДЭК" value={`${Number(cdekDetail.delivery_sum).toLocaleString('ru-RU')} ₽`} />
            )}
            {cdekDetail?.total_sum != null && (
              <FieldRow label="Всего по накладной" value={`${Number(cdekDetail.total_sum).toLocaleString('ru-RU')} ₽`} />
            )}
            {(cdekTracking.is_return || cdekTracking.is_reverse) && (
              <FieldRow label="Тип" value={cdekTracking.is_return ? 'Возвратный заказ' : 'Реверсный заказ'} />
            )}
            {cdekTracking.comment && <FieldRow label="Комментарий" value={cdekTracking.comment} />}
          </div>

          {(cdekProblems.length > 0 || cdekCalls.failed_calls?.length || cdekCalls.rescheduled_calls?.length) && (
            <div className="mx-5 mb-5 p-4 border border-st-unpaid/25 bg-st-unpaid/5 text-[11px]">
              <div className="eyebrow text-st-unpaid mb-2">Проблемы и связь</div>
              {cdekProblems.map((problem, index) => <div key={`problem-${index}`}>Проблема {problem.code || '—'} · {cdekDate(problem.create_date, true)}</div>)}
              {cdekCalls.failed_calls?.map((call, index) => <div key={`failed-${index}`}>Не дозвонились · причина {call.reason_code ?? '—'} · {cdekDate(call.date_time, true)}</div>)}
              {cdekCalls.rescheduled_calls?.map((call, index) => <div key={`rescheduled-${index}`}>Перезвон: {cdekDate(call.date_next)} {call.time_next || ''} {call.comment || ''}</div>)}
            </div>
          )}

          {cdekTracking.statuses?.length ? (
            <details className="border-t border-rule">
              <summary className="px-4 sm:px-6 py-3 cursor-pointer text-[11px] font-semibold text-ink-muted hover:text-ink">
                История движения · {cdekTracking.statuses.length}
              </summary>
              <div className="px-4 sm:px-6 pb-5 max-h-[360px] overflow-y-auto space-y-0">
                {cdekTracking.statuses.map((status, index) => (
                  <div key={`${status.code}-${status.date_time}-${index}`} className="grid grid-cols-1 sm:grid-cols-[115px_1fr_95px] gap-1 sm:gap-3 py-2.5 border-t border-rule/60 text-[11px]">
                    <span className="text-ink-faint tab-nums">{cdekDate(status.date_time, true)}</span>
                    <span className="font-medium text-ink-2">{status.name}</span>
                    <span className="text-right text-ink-muted truncate">{status.city || EM_DASH}</span>
                  </div>
                ))}
              </div>
            </details>
          ) : null}
          </div>
        </OutboundCdekSection>
      )}
      </div>{/* end left column */}

      {/* Right column — messages */}
      <div>
      {sendError && (
        <div className="mb-5 px-4 py-3 rounded-md border border-st-unpaid/30 bg-st-unpaid/8 flex items-baseline gap-3 flex-wrap">
          <span className="text-[11px] tracking-[0.08em] uppercase text-st-unpaid font-semibold">
            Ошибка отправки
          </span>
          <span className="text-[13px] text-ink-2">{sendError}</span>
        </div>
      )}

      {nameParts.length > 1 && (() => {
        const effectiveIdx = greetingNameIdx !== null && greetingNameIdx < nameParts.length
          ? greetingNameIdx
          : guessGreetingIndex(nameParts)
        return (
          <div className="mb-5 px-4 py-3.5 rounded-md border border-rule bg-bone/40">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="text-[11px] tracking-[0.08em] uppercase text-ink-muted font-semibold">
                Обращение в сообщениях
              </span>
              <span className="text-[11px] text-ink-faint">· как обращаться в «Здравствуйте, …»</span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {nameParts.map((part, i) => (
                <button
                  key={i}
                  onClick={() => onGreetingPartChange(i)}
                  className={`px-2.5 py-1 rounded-md text-[12px] font-medium border transition-colors ${
                    effectiveIdx === i
                      ? 'bg-gold/15 text-gold-deep border-gold/30'
                      : 'bg-bone text-ink-muted border-rule-strong hover:text-ink hover:border-ink-faint'
                  }`}
                >
                  {capitalize(part)}
                </button>
              ))}
            </div>
          </div>
        )
      })()}

      <MessageBlock
        title="Предуведомление"
        subtitle="До отправки посылки"
        messageState={messageStateFor({ messageStates }, 'firstMsg')}
        onToggleManualMark={() => onToggleManualMark('firstMsg')}
        markingManual={markingManual === 'firstMsg'}
        text={firstMessageText}
        missing={firstMsgMissing}
        copied={copiedFirst}
        onCopy={onCopyFirst}
        onSend={firstMessageText ? onSendFirst : undefined}
        sending={sendingType === 'firstMsg'}
        sendingDisabled={sendingType !== null}
      />

      {handoffAccepted && <div className="mb-10">
        <MessageHeader
          title="Подтверждение отправки"
          subtitle="С трек-номером и датой доставки"
          messageState={messageStateFor({ messageStates }, 'confirmMsg')}
          onToggleManualMark={() => onToggleManualMark('confirmMsg')}
          markingManual={markingManual === 'confirmMsg'}
          actions={
            templateText && (
              <>
                <button
                  onClick={onCopy}
                  disabled={!isReady}
                  className={`btn btn-sm ${copied ? 'btn-gold' : 'btn-ghost'}`}
                >
                  {copied ? 'Скопировано' : 'Копировать'}
                </button>
                <button
                  onClick={(event) => onSendConfirm({ x: event.clientX, y: event.clientY })}
                  disabled={!isReady || sendingType !== null}
                  className={`btn btn-sm ${sendingType === 'confirmMsg' ? 'btn-ghost cursor-wait' : 'btn-primary'}`}
                >
                  {sendingType === 'confirmMsg'
                    ? 'Отправка…'
                    : messageStateFor({ messageStates }, 'confirmMsg')
                      ? 'Отправить снова'
                      : 'Отправить'}
                </button>
              </>
            )
          }
        />

        {!isReady && missingFields.length > 0 && (
          <div className="mb-4 px-4 py-3 rounded-md border border-rule-strong bg-bone/60 flex items-center gap-2 flex-wrap">
            <span className="text-[11px] tracking-[0.08em] uppercase text-ink-muted font-semibold">
              Не заполнено
            </span>
            {missingFields.map((f) => (
              <span key={f} className="px-2 py-0.5 rounded-sm bg-paper border border-rule-strong text-ink-2 text-[12px]">
                {f}
              </span>
            ))}
          </div>
        )}

        {templateText && messageStateFor({ messageStates }, 'confirmMsg') && <SentTextNote />}
        {templateText && (
          <div className="surface-card rounded-md p-4 sm:p-6 text-[14px] leading-[1.6] whitespace-pre-wrap break-words text-ink-2">
            {templateText}
          </div>
        )}
      </div>}

      <MessageBlock
        title="Уведомление о курьере"
        subtitle="Постуведомление после назначения курьера"
        messageState={messageStateFor({ messageStates }, 'courierMsg')}
        onToggleManualMark={() => onToggleManualMark('courierMsg')}
        markingManual={markingManual === 'courierMsg'}
        text={courierMessageText}
        missing={[]}
        copied={copiedCourier}
        onCopy={onCopyCourier}
        onSend={courierMessageText ? onSendCourier : undefined}
        sending={sendingType === 'courierMsg'}
        sendingDisabled={sendingType !== null}
        emptyText={courierMessageEmptyText(cdekTracking)}
      />
      <YMarketChatPanel history={chatHistory} loading={chatLoading} error={chatError} open={chatOpen} pending={pendingChatDelivery} flight={chatSendFlight} onFlightComplete={onChatSendFlightComplete} onOpenChange={onChatOpenChange} onRefresh={onRefreshChat} />
      </div>{/* end right column */}
    </div>
  )
}
